import React from 'react';
import { User, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import type { ChatMessage, QuickReply } from '../../hooks/useRoary';
import roaryMascot from '/assets/Roary-mascot.png';

interface RoaryMessageProps {
  message: ChatMessage;
  darkMode?: boolean;
  onQuickReply?: (reply: QuickReply) => void;
}

// Format tool name for display - human-friendly mappings
const formatToolName = (name: string): string => {
  const mappings: Record<string, string> = {
    // Site operations
    create_site: 'Creating website',
    update_site: 'Updating settings',
    get_site: 'Loading site',
    list_user_sites: 'Loading sites',
    update_site_settings: 'Configuring settings',
    get_available_themes: 'Loading themes',
    get_site_summary: 'Reviewing site',
    // Page operations
    create_page: 'Building page',
    update_page: 'Updating page',
    delete_page: 'Removing page',
    list_pages: 'Loading pages',
    // Component operations
    add_component: 'Adding content',
    add_layout_component: 'Adding to layout',
    update_component: 'Updating section',
    delete_component: 'Removing section',
    list_components: 'Loading components',
    reorder_components: 'Organizing sections',
    get_component_schema: 'Preparing component',
    // Layout operations
    create_layout: 'Setting up layout',
    assign_layout: 'Applying layout',
    assign_layout_to_page: 'Applying layout',
    list_layouts: 'Loading layouts',
    update_layout: 'Updating layout',
    // Blog operations
    create_blog: 'Setting up blog',
    create_blog_post: 'Writing post',
    update_blog_post: 'Updating post',
    list_blog_posts: 'Loading posts',
    // Navigation
    update_navbar: 'Configuring navigation',
    update_footer: 'Setting up footer',
    // Media
    search_stock_images: 'Finding images',
    upload_image: 'Uploading image',
  };
  return mappings[name] || name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
};

// Simple markdown renderer for message content
const renderMarkdown = (text: string, darkMode: boolean): React.ReactNode => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentList: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  // Base text color for the mode
  const textColor = darkMode ? 'text-white' : 'text-gray-800';

  const flushList = () => {
    if (currentList.length > 0 && listType) {
      const ListTag = listType;
      elements.push(
        <ListTag
          key={`list-${elements.length}`}
          className={`my-2 ml-4 space-y-1 ${listType === 'ul' ? 'list-disc' : 'list-decimal'} ${textColor}`}
        >
          {currentList.map((item, i) => (
            <li key={i} className="text-sm">
              {renderInline(item, darkMode)}
            </li>
          ))}
        </ListTag>
      );
      currentList = [];
      listType = null;
    }
  };

  const renderInline = (line: string, dark: boolean): React.ReactNode => {
    // Handle bold **text**
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong
            key={i}
            className={dark ? 'text-orange-300 font-semibold' : 'text-gray-900 font-semibold'}
          >
            {part.slice(2, -2)}
          </strong>
        );
      }
      // Return text as-is (will inherit color from parent)
      return part;
    });
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    // Unordered list item
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(trimmed.slice(2));
      return;
    }

    // Ordered list item
    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(orderedMatch[1]);
      return;
    }

    // A blank line between items of the same list keeps that list going,
    // so numbered questions stay 1, 2, 3 instead of restarting at 1.
    if (!trimmed) {
      const next = lines.slice(index + 1).find(candidate => candidate.trim());
      const nextTrimmed = next?.trim() ?? '';
      const continuesOrdered = listType === 'ol' && /^\d+\.\s+/.test(nextTrimmed);
      const continuesUnordered =
        listType === 'ul' && (nextTrimmed.startsWith('- ') || nextTrimmed.startsWith('• '));
      if (continuesOrdered || continuesUnordered) return;

      flushList();
      elements.push(<br key={`br-${index}`} />);
      return;
    }

    // Not a list item - flush any pending list
    flushList();

    // Regular paragraph with explicit text color
    elements.push(
      <p key={`p-${index}`} className={`text-sm leading-relaxed mb-2 last:mb-0 ${textColor}`}>
        {renderInline(trimmed, darkMode)}
      </p>
    );
  });

  // Flush any remaining list
  flushList();

  return <>{elements}</>;
};

// Quick reply buttons component
const QuickReplyButtons: React.FC<{
  replies: QuickReply[];
  darkMode: boolean;
  onQuickReply?: (reply: QuickReply) => void;
}> = ({ replies, darkMode, onQuickReply }) => {
  if (!onQuickReply) return null;

  return (
    <div className="flex flex-col items-center gap-3 mt-5">
      {replies.map(reply => (
        <button
          key={reply.id}
          type="button"
          disabled={reply.disabled}
          onClick={() => {
            if (reply.disabled) return;
            onQuickReply(reply);
          }}
          className={`
            w-full max-w-sm px-5 py-2.5 rounded-xl text-base font-medium
            transition-all duration-200
            ${
              reply.disabled
                ? darkMode
                  ? 'bg-transparent border border-slate-700 text-gray-500 cursor-not-allowed'
                  : 'bg-white border border-gray-200 text-gray-400 cursor-not-allowed'
                : reply.variant === 'primary'
                  ? darkMode
                    ? 'bg-orange-500/20 border border-orange-500/50 text-orange-300 hover:bg-orange-500/30 hover:border-orange-400'
                    : 'bg-orange-50 border border-orange-400 text-orange-700 hover:bg-orange-100'
                  : darkMode
                    ? 'bg-transparent border border-slate-600 text-gray-300 hover:bg-slate-700 hover:border-slate-500'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }
          `}
        >
          <span className={reply.disabled ? 'line-through' : undefined}>
            {reply.icon && <span className="mr-2 text-lg">{reply.icon}</span>}
            {reply.label}
          </span>
          {reply.disabled && <span className="block text-xs font-normal mt-0.5">Coming Soon</span>}
        </button>
      ))}
    </div>
  );
};

export const RoaryMessage: React.FC<RoaryMessageProps> = ({
  message,
  darkMode = false,
  onQuickReply,
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  // Don't render empty assistant messages (e.g. placeholders left after errors)
  const hasContent = !!message.content;
  const hasQuickReplies = message.quickReplies && message.quickReplies.length > 0;
  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;
  if (!isUser && !message.isStreaming && !hasContent && !hasQuickReplies && !hasToolCalls) {
    return null;
  }

  // Determine text color based on role and mode
  const getTextColor = () => {
    if (isUser) return 'text-white';
    if (isSystem) return darkMode ? 'text-gray-300' : 'text-gray-700';
    return darkMode ? 'text-white' : 'text-gray-800';
  };

  return (
    <div className="flex flex-col gap-1 max-w-3xl mx-auto">
      <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <div
          className={`flex-shrink-0 rounded-full flex items-center justify-center overflow-hidden ${
            isUser
              ? `w-10 h-10 ${darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`
              : isSystem
                ? `w-10 h-10 ${darkMode ? 'bg-slate-700 text-gray-400' : 'bg-gray-100 text-gray-600'}`
                : 'w-14 h-14'
          }`}
        >
          {isUser ? (
            <User className="w-5 h-5" />
          ) : isSystem ? (
            <User className="w-5 h-5" />
          ) : (
            <img src={roaryMascot} alt="Roary" className="w-14 h-14 object-contain" />
          )}
        </div>

        {/* Message content */}
        <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
          {/* Message bubble */}
          <div
            className={`inline-block rounded-2xl px-4 py-3 ${
              isUser
                ? 'bg-blue-600 rounded-br-md'
                : isSystem
                  ? darkMode
                    ? 'bg-slate-800 rounded-bl-md'
                    : 'bg-gray-100 rounded-bl-md'
                  : darkMode
                    ? 'bg-slate-700 border border-slate-600 rounded-bl-md shadow-lg'
                    : 'bg-white border border-gray-200 rounded-bl-md shadow-sm'
            }`}
          >
            {/* Message text */}
            <div className={getTextColor()}>
              {message.content ? renderMarkdown(message.content, darkMode) : null}
            </div>

            {/* Streaming indicator */}
            {message.isStreaming && !message.content && (
              <div
                className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <span className="animate-pulse">Thinking</span>
                <span className="flex gap-0.5">
                  <span
                    className={`w-1 h-1 rounded-full animate-bounce ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}`}
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className={`w-1 h-1 rounded-full animate-bounce ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}`}
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className={`w-1 h-1 rounded-full animate-bounce ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}`}
                    style={{ animationDelay: '300ms' }}
                  />
                </span>
              </div>
            )}
          </div>

          {/* Tool calls */}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.toolCalls.map((tool, index) => (
                <div
                  key={`${tool.name}-${index}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                    tool.status === 'running'
                      ? darkMode
                        ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                        : 'bg-blue-50 text-blue-700'
                      : tool.status === 'completed'
                        ? darkMode
                          ? 'bg-green-500/10 text-green-400'
                          : 'bg-green-50 text-green-700'
                        : darkMode
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-red-50 text-red-700'
                  }`}
                >
                  {tool.status === 'running' ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : tool.status === 'completed' ? (
                    <CheckCircle className="w-3 h-3" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  <span>{formatToolName(tool.name)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Timestamp */}
          <p
            className={`text-xs mt-1 ${isUser ? 'text-right' : ''} ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
          >
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      {hasQuickReplies && (
        <QuickReplyButtons
          replies={message.quickReplies!}
          darkMode={darkMode}
          onQuickReply={onQuickReply}
        />
      )}
    </div>
  );
};
